/**
 * Face-edge kernel — the arrangement core (K0, spike §4.1).
 *
 * `insertSegment` is the ONE primitive every drawing tool funnels into (pencil
 * Line, Rectangle, Circle segments, push/pull side edges, Follow Me output):
 *
 *   1. materialize endpoints — weld to vertices within WELD_TOL, or split an
 *      edge whose interior the point lands on (T-junction start);
 *   2. find crossings/overlaps with existing edges — split them at welded
 *      vertices so NO two edges ever cross away from a shared vertex (the
 *      arrangement invariant);
 *   3. chain the sub-segments as edges (retraced spans dedupe — and still seed
 *      healing: SketchUp's retrace-to-reheal);
 *   4. faces whose interior a new edge crosses REPARTITION (subdivision — Q13);
 *   5. loose new/retraced edges attempt LOOP HEALING on candidate planes —
 *      bounded regions not already faced become faces.
 *
 * Erasing: an edge takes its dependent faces with it; a face keeps its edges
 * (SketchUp exactly). This module is policy over topo.ts's structure layer and
 * trace2d.ts's planar tracing; it stores nothing and renders nothing.
 */

import type { BodyTopology, BodyPt3, BodyVid, BodyEid, BodyFid, BodyLoop, BodyPlane, ArrangeResult } from './types'
import { facePaint, reverseFaceInPlace } from './paint'
import {
  WELD_TOL, dist, sub, lerp, planeDist, planeFromPoints, planeFromPointDirs,
  canonicalPlane, samePlane, frameForPlane, to2, from2, pointSegDist, segSegClosest,
} from './geom3'
import type { Pt2, PlaneFrame } from './geom3'
import {
  newVid, newFid, weldVertex, findVertexNear, addEdge, splitEdge, gcOrphanVertices,
  facesUsingEdge, faceRings2, pointInRegion, distToRings, regionInteriorPoint, loopPoints,
} from './topo'
import { traceFaces } from './trace2d'
import type { TraceNode, TraceEdge, TracedFace } from './trace2d'

const PLANE_TOL = WELD_TOL * 2

// ---------------------------------------------------------------------------
// insertSegment
// ---------------------------------------------------------------------------

export function insertSegment(topo: BodyTopology, A: BodyPt3, B: BodyPt3): ArrangeResult {
  const result: ArrangeResult = { addedEdges: [], retracedEdges: [], addedFaces: [], removedFaces: [] }

  const va = materializePoint(topo, A)
  const vb = materializePoint(topo, B)
  if (va === vb) { gcOrphanVertices(topo); return result }

  const pa = topo.vertices[va]
  const pb = topo.vertices[vb]

  // ---- events along the segment: t → vertex --------------------------------
  const events = new Map<BodyVid, number>()
  events.set(va, 0)
  events.set(vb, 1)

  // (a) pre-existing vertices lying on the open segment
  for (const [vid, p] of Object.entries(topo.vertices)) {
    if (vid === va || vid === vb) continue
    const { d, t } = pointSegDist(p, pa, pb)
    if (d <= WELD_TOL && t > 0 && t < 1) events.set(vid, t)
  }

  // (b) crossings + collinear overlaps against a snapshot of the edge set
  const pendingSplits = new Map<BodyEid, BodyVid[]>()
  const queueSplit = (eid: BodyEid, vid: BodyVid) => {
    const list = pendingSplits.get(eid) || []
    if (!list.includes(vid)) list.push(vid)
    pendingSplits.set(eid, list)
  }
  for (const [eid, e] of Object.entries({ ...topo.edges })) {
    if (!topo.edges[eid]) continue
    const ea = topo.vertices[e.a]
    const eb = topo.vertices[e.b]
    const r = segSegClosest(pa, pb, ea, eb)
    if (r.d > WELD_TOL) continue
    if (r.parallel) {
      // collinear overlap: the other edge's endpoints event onto the segment;
      // our endpoints split the other edge where they land on its interior
      for (const vid of [e.a, e.b]) {
        if (events.has(vid)) continue
        const { d, t } = pointSegDist(topo.vertices[vid], pa, pb)
        if (d <= WELD_TOL && t > 0 && t < 1) events.set(vid, t)
      }
      for (const vid of [va, vb]) {
        const p = topo.vertices[vid]
        const { d } = pointSegDist(p, ea, eb)
        if (d <= WELD_TOL && dist(p, ea) > WELD_TOL && dist(p, eb) > WELD_TOL) queueSplit(eid, vid)
      }
      continue
    }
    // proper (or T) crossing at one point
    const c = lerp(lerp(pa, pb, r.s), lerp(ea, eb, r.t), 0.5)
    const vid = weldVertex(topo, c)
    const { t: tAB } = pointSegDist(topo.vertices[vid], pa, pb)
    if (!events.has(vid)) events.set(vid, vid === va ? 0 : vid === vb ? 1 : tAB)
    if (vid !== e.a && vid !== e.b) queueSplit(eid, vid)
  }

  // apply the queued splits — per edge, ordered from its own a-end
  for (const [eid, vids] of pendingSplits) {
    if (!topo.edges[eid]) continue
    const e = topo.edges[eid]
    const a0 = topo.vertices[e.a]
    const ordered = [...vids].sort(
      (x, y) => dist(topo.vertices[x], a0) - dist(topo.vertices[y], a0),
    )
    let cur = eid
    for (const vid of ordered) {
      const ce = topo.edges[cur]
      if (!ce || vid === ce.a || vid === ce.b) continue
      const [, tail] = splitEdge(topo, cur, vid)
      cur = tail
    }
  }

  // ---- chain the sub-segments ----------------------------------------------
  const chain = [...events.entries()].sort((x, y) => x[1] - y[1]).map(([vid]) => vid)
  for (let i = 0; i + 1 < chain.length; i++) {
    if (chain[i] === chain[i + 1]) continue
    const added = addEdge(topo, chain[i], chain[i + 1])
    if (!added) continue
    if (added.existed) result.retracedEdges.push(added.eid)
    else result.addedEdges.push(added.eid)
  }

  // ---- subdivision: repartition faces a new edge crosses --------------------
  const seedEids = [...result.addedEdges, ...result.retracedEdges]
  const affectedFaces = new Set<BodyFid>()
  for (const [fid, face] of Object.entries(topo.faces)) {
    const { frame, rings } = faceRings2(topo, face)
    for (const eid of seedEids) {
      const e = topo.edges[eid]
      if (!e) continue
      const p1 = topo.vertices[e.a]
      const p2 = topo.vertices[e.b]
      if (Math.abs(planeDist(face.plane, p1)) > PLANE_TOL) continue
      if (Math.abs(planeDist(face.plane, p2)) > PLANE_TOL) continue
      const mid = to2(frame, lerp(p1, p2, 0.5))
      if (pointInRegion(mid, rings[0], rings.slice(1)) && distToRings(mid, rings) > WELD_TOL) {
        affectedFaces.add(fid)
        break
      }
    }
  }
  for (const fid of affectedFaces) {
    const r = repartitionFace(topo, fid)
    result.addedFaces.push(...r.added)
    result.removedFaces.push(...r.removed)
  }

  // ---- healing: loose loops become faces ------------------------------------
  const healed = healFromEdges(topo, seedEids)
  result.addedFaces.push(...healed)

  gcOrphanVertices(topo)
  return result
}

/** weld p to a vertex, or land it on an edge's interior (splitting there), or create it */
function materializePoint(topo: BodyTopology, p: BodyPt3): BodyVid {
  const near = findVertexNear(topo, p)
  if (near) return near
  for (const [eid, e] of Object.entries(topo.edges)) {
    const ea = topo.vertices[e.a]
    const eb = topo.vertices[e.b]
    const { d, t } = pointSegDist(p, ea, eb)
    if (d > WELD_TOL) continue
    const proj = lerp(ea, eb, t)
    if (dist(proj, ea) <= WELD_TOL || dist(proj, eb) <= WELD_TOL) continue
    const vid = newVid()
    topo.vertices[vid] = proj
    splitEdge(topo, eid, vid)
    return vid
  }
  const vid = newVid()
  topo.vertices[vid] = { x: p.x, y: p.y, z: p.z }
  return vid
}

// ---------------------------------------------------------------------------
// Face repartition (subdivision)
// ---------------------------------------------------------------------------

function repartitionFace(topo: BodyTopology, fid: BodyFid): { added: BodyFid[]; removed: BodyFid[] } {
  const face = topo.faces[fid]
  const { frame, rings } = faceRings2(topo, face)
  const outer = rings[0]
  const holes = rings.slice(1)

  // participants: the face's own loop edges + every edge lying in its region
  const edgeIds = new Set<BodyEid>()
  for (const loop of face.loops) for (const eid of loop.edges) edgeIds.add(eid)
  for (const [eid, e] of Object.entries(topo.edges)) {
    if (edgeIds.has(eid)) continue
    const p1 = topo.vertices[e.a]
    const p2 = topo.vertices[e.b]
    if (Math.abs(planeDist(face.plane, p1)) > PLANE_TOL) continue
    if (Math.abs(planeDist(face.plane, p2)) > PLANE_TOL) continue
    const mid = to2(frame, lerp(p1, p2, 0.5))
    if (pointInRegion(mid, outer, holes)) edgeIds.add(eid)
  }

  const traced = traceGraph(topo, frame, edgeIds)
  const kept = traced.filter((t) => {
    const ip = regionInteriorPoint(t.outer.pts, t.holes.map((h) => h.pts))
    return !!ip && pointInRegion(ip, outer, holes)
  })

  // no-change: one piece using exactly the original loops' edge multiset
  if (kept.length === 1) {
    const origEdges = face.loops.flatMap((l) => l.edges).sort()
    const newEdges = [kept[0].outer, ...kept[0].holes].flatMap((l) => l.edges).sort()
    if (origEdges.length === newEdges.length && origEdges.every((v, i) => v === newEdges[i])) {
      return { added: [], removed: [] }
    }
  }
  if (!kept.length) return { added: [], removed: [] } // pathological — keep the face rather than lose it

  delete topo.faces[fid]
  const added: BodyFid[] = []
  for (const t of kept) {
    const nf = newFid()
    topo.faces[nf] = {
      plane: face.plane,
      loops: tracedToLoops(t),
      ...facePaint(face),
    }
    added.push(nf)
  }
  return { added, removed: [fid] }
}

// ---------------------------------------------------------------------------
// Healing
// ---------------------------------------------------------------------------

function healFromEdges(topo: BodyTopology, seeds: BodyEid[]): BodyFid[] {
  const liveSeeds = seeds.filter((eid) => topo.edges[eid])
  if (!liveSeeds.length) return []

  // candidate planes: spanned by a seed and each edge sharing an endpoint, plus
  // planes of faces containing both seed endpoints
  const candidates: BodyPlane[] = []
  const pushPlane = (pl: BodyPlane | null) => { if (pl) candidates.push(canonicalPlane(pl)) }
  for (const seedEid of liveSeeds) {
    const e = topo.edges[seedEid]
    const pa = topo.vertices[e.a]
    const pb = topo.vertices[e.b]
    const de = sub(pb, pa)
    for (const [oid, o] of Object.entries(topo.edges)) {
      if (oid === seedEid) continue
      let at: BodyPt3 | null = null
      if (o.a === e.a || o.b === e.a) at = pa
      else if (o.a === e.b || o.b === e.b) at = pb
      if (!at) continue
      pushPlane(planeFromPointDirs(at, de, sub(topo.vertices[o.b], topo.vertices[o.a])))
    }
    for (const f of Object.values(topo.faces)) {
      if (Math.abs(planeDist(f.plane, pa)) <= PLANE_TOL && Math.abs(planeDist(f.plane, pb)) <= PLANE_TOL) {
        pushPlane(f.plane)
      }
    }
  }

  // dedupe planes
  const uniquePlanes: BodyPlane[] = []
  for (const pl of candidates) {
    if (!uniquePlanes.some((u) => samePlane(u, pl))) uniquePlanes.push(pl)
  }

  const seedSet = new Set(liveSeeds)
  const added: BodyFid[] = []
  for (const plane of uniquePlanes) {
    const frame = frameForPlane(plane)
    // every edge on this plane
    const edgeIds = new Set<BodyEid>()
    for (const [eid, e] of Object.entries(topo.edges)) {
      if (Math.abs(planeDist(plane, topo.vertices[e.a])) > PLANE_TOL) continue
      if (Math.abs(planeDist(plane, topo.vertices[e.b])) > PLANE_TOL) continue
      edgeIds.add(eid)
    }
    if (![...edgeIds].some((eid) => seedSet.has(eid))) continue

    // existing faces on this plane, projected into THIS frame for overlap tests
    const existing: { outer: Pt2[]; holes: Pt2[][] }[] = []
    for (const f of Object.values(topo.faces)) {
      if (!samePlane(f.plane, plane)) continue
      const rings = f.loops.map((l) => loopPoints(topo, l).map((p) => to2(frame, p)))
      existing.push({ outer: rings[0], holes: rings.slice(1) })
    }

    for (const t of traceGraph(topo, frame, edgeIds)) {
      const tEdges = [t.outer, ...t.holes].flatMap((l) => l.edges)
      if (!tEdges.some((eid) => seedSet.has(eid))) continue
      const ipT = regionInteriorPoint(t.outer.pts, t.holes.map((h) => h.pts))
      if (!ipT) continue
      let overlaps = false
      for (const ex of existing) {
        if (pointInRegion(ipT, ex.outer, ex.holes)) { overlaps = true; break }
        const ipF = regionInteriorPoint(ex.outer, ex.holes)
        if (ipF && pointInRegion(ipF, t.outer.pts, t.holes.map((h) => h.pts))) { overlaps = true; break }
      }
      if (overlaps) continue

      // refit the plane from the actual outer ring (accuracy), keeping n aligned
      const outer3 = loopVertsToPoints(topo, t.outer.edges, t.outer.dirs)
      const refit = planeFromPoints(outer3)
      const usePlane =
        refit && refit.n.x * plane.n.x + refit.n.y * plane.n.y + refit.n.z * plane.n.z > 0
          ? refit
          : refit
            ? { n: { x: -refit.n.x, y: -refit.n.y, z: -refit.n.z }, d: -refit.d }
            : plane

      const nf = newFid()
      topo.faces[nf] = { plane: usePlane, loops: tracedToLoops(t) }
      existing.push({ outer: t.outer.pts, holes: t.holes.map((h) => h.pts) })
      added.push(nf)
    }
  }
  return added
}

// ---------------------------------------------------------------------------
// Erasing (SketchUp semantics)
// ---------------------------------------------------------------------------

/** erasing an edge deletes the faces that depend on it; edges stay welded */
/**
 * Erase an edge. HEAL ON ERASE (2026-09-28, Matt: "I'd like to be able to delete
 * interior lines of faces and have those faces automatically heal when they can"):
 * SketchUp's rule — the faces the edge bounded go, and what they leave behind heals
 * back wherever it still closes. Per plane the edge touched, the removed faces' own
 * remaining edges are re-traced; a traced region is rebuilt only if it is made
 * ENTIRELY of the removed faces (its area equals the sum of the old faces inside it),
 * so a merge never fills an opening. Two coplanar faces split by the line merge
 * into one; a face that only touched the line at a pinch comes back whole; a face
 * that really lost a side (the line was its edge against empty space) stays gone.
 * A rebuilt face takes the orientation and paint of the largest old face in it.
 * -> { removedFaces, healedFaces }
 */
export function eraseEdge(topo: BodyTopology, eid: BodyEid): { removedFaces: BodyFid[]; healedFaces: BodyFid[] } {
  const removedIds = facesUsingEdge(topo, eid)
  const netArea = (outer: Pt2[], holes: Pt2[][]) => {
    const a = (r: Pt2[]) => Math.abs(r.reduce((s, p, i) => { const q = r[(i + 1) % r.length]; return s + p.x * q.y - q.x * p.y }, 0) / 2)
    return a(outer) - holes.reduce((s, h) => s + a(h), 0)
  }
  const olds = removedIds.map((fid) => {
    const face = topo.faces[fid]
    const { frame, rings } = faceRings2(topo, face)
    const ip = regionInteriorPoint(rings[0], rings.slice(1))
    return { fid, face, area: netArea(rings[0], rings.slice(1)), ip3: ip ? from2(frame, ip) : null }
  })
  const groups: (typeof olds)[] = []
  for (const o of olds) {
    const g = groups.find((x) => samePlane(x[0].face.plane, o.face.plane))
    if (g) g.push(o)
    else groups.push([o])
  }
  for (const fid of removedIds) delete topo.faces[fid]
  delete topo.edges[eid]
  gcOrphanVertices(topo)

  const healed: BodyFid[] = []
  for (const g of groups) {
    const basePlane = g[0].face.plane
    const frame = frameForPlane(basePlane)
    const edgeIds = new Set<BodyEid>()
    for (const o of g) for (const l of o.face.loops) for (const e of l.edges) if (topo.edges[e]) edgeIds.add(e)
    if (!edgeIds.size) continue
    for (const t of traceGraph(topo, frame, edgeIds)) {
      const outer = t.outer.pts
      const holes = t.holes.map((h) => h.pts)
      const A = netArea(outer, holes)
      if (!(A > WELD_TOL * WELD_TOL)) continue
      const inside = g.filter((o) => o.ip3 && pointInRegion(to2(frame, o.ip3), outer, holes))
      const sum = inside.reduce((s2, o) => s2 + o.area, 0)
      if (!inside.length || Math.abs(sum - A) > 1e-6 * Math.max(1, A)) continue   // it would cover a gap
      const base = [...inside].sort((a, b) => b.area - a.area)[0]
      const nf = newFid()
      topo.faces[nf] = { plane: { n: { ...basePlane.n }, d: basePlane.d }, loops: tracedToLoops(t) }
      const n0 = base.face.plane.n
      if (basePlane.n.x * n0.x + basePlane.n.y * n0.y + basePlane.n.z * n0.z < 0) reverseFaceInPlace(topo, nf)
      Object.assign(topo.faces[nf], facePaint(base.face))
      healed.push(nf)
    }
  }
  return { removedFaces: removedIds, healedFaces: healed }
}

/** erasing a face keeps its edges (retrace an edge to heal it back) */
export function eraseFace(topo: BodyTopology, fid: BodyFid): boolean {
  if (!topo.faces[fid]) return false
  delete topo.faces[fid]
  return true
}

// ---------------------------------------------------------------------------
// shared helpers
// ---------------------------------------------------------------------------

function traceGraph(topo: BodyTopology, frame: PlaneFrame, edgeIds: Set<BodyEid>): TracedFace[] {
  const nodeIds = new Set<BodyVid>()
  const edges: TraceEdge[] = []
  for (const eid of edgeIds) {
    const e = topo.edges[eid]
    if (!e) continue
    nodeIds.add(e.a)
    nodeIds.add(e.b)
    edges.push({ id: eid, a: e.a, b: e.b })
  }
  const nodes: TraceNode[] = [...nodeIds].map((vid) => ({ id: vid, pt: to2(frame, topo.vertices[vid]) }))
  return traceFaces(nodes, edges)
}

function tracedToLoops(t: TracedFace): BodyLoop[] {
  return [
    { edges: [...t.outer.edges], dirs: [...t.outer.dirs] },
    ...t.holes.map((h) => ({ edges: [...h.edges], dirs: [...h.dirs] })),
  ]
}

function loopVertsToPoints(topo: BodyTopology, edges: BodyEid[], dirs: boolean[]): BodyPt3[] {
  const out: BodyPt3[] = []
  for (let i = 0; i < edges.length; i++) {
    const e = topo.edges[edges[i]]
    out.push(topo.vertices[dirs[i] ? e.a : e.b])
  }
  return out
}
